import React, { useEffect, useState, useRef } from 'react';
import {
  Trigger,
  Badge,
  Spin,
} from '@arco-design/web-react';
import MessageList, { MessageListType, MessageItemData } from './list';
import styles from './style/index.module.less';
import { getNotifications, markNotificationAsRead, getUnreadNotificationCount } from '@/api/notifications';
import type { Notification } from '@/types/notification';
import { useHistory } from 'react-router-dom';
import { supabase } from '@/lib/supabase/client';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { createLogger } from '@/utils/logger';

const listLogger = createLogger('MessageBoxList');
const badgeLogger = createLogger('MessageBoxBadge');

type NotificationWithBusiness = Notification & {
  business_name?: string;
};

function DropContent() {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState<MessageListType>([]);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const closingChannelsRef = useRef(new Set<RealtimeChannel>());
  const reconnectTimerRef = useRef<number | null>(null);
  const isUnmountedRef = useRef(false);

  async function fetchNotifications(showLoading = true) {
    showLoading && setLoading(true);
    try {
      const result = await getNotifications({ page: 1, pageSize: 20 });

      // 通知窗口显示规则：
      // 1. 显示所有 pending 通知
      // 2. 显示未读的 approved/rejected/cc 通知
      // 3. 不显示已读的 approved/rejected/cc 通知
      const filteredData = result.data.filter((notification) => {
        if (notification.notification_type === 'pending') {
          // pending 通知全部显示（不区分已读/未读）
          return true;
        } else {
          // approved/rejected/cc 通知只显示未读的
          return !notification.is_read;
        }
      });

      // 转换通知数据为 MessageList 格式
      const messageData: MessageListType = filteredData.map((notification) => ({
        id: notification.id,
        title: getNotificationTitle(notification),
        subTitle: '', // 不显示副标题
        content: getNotificationContent(notification),
        time: formatTime(notification.created_at),
        status: notification.is_read ? 1 : 0,
        tag: {
          text: getNotificationTypeText(notification.notification_type),
          color: getNotificationTypeColor(notification.notification_type),
        },
        // 保存原始数据用于跳转和处理
        approvalId: notification.approval_id,
        notificationType: notification.notification_type,
      }));
      setNotifications(messageData);
    } catch (error) {
      listLogger.error('获取通知失败', error);
    } finally {
      showLoading && setLoading(false);
    }
  }

  function getNotificationTitle(notification: Notification): string {
    // 审批类型映射
    const typeMap: Record<string, string> = {
      'project_create': '项目创建审批',
      'project_change': '项目变更审批',
      'milestone_completion': '里程碑完成审批',
      'project_post_record': '项目补录审批',
      'design_confirm': '设计确认审批',
      'contribution_create': '专业贡献立项审批',
      'contribution_acceptance': '专业贡献验收审批',
      'performance_acceptance': '业绩验收审批',
    };
    return typeMap[notification.approval_type] || '审批通知';
  }

  function getNotificationContent(notification: NotificationWithBusiness): string {
    const type = notification.notification_type;
    const applicantName = notification.applicant_name || '申请人';
    const businessName = notification.business_name || '业务数据';
    const approvalType = notification.approval_type;

    // 根据审批类型生成不同的通知内容
    switch (approvalType) {
      case 'milestone_completion':
        switch (type) {
          case 'pending':
            return `${applicantName}提交【${businessName}】里程碑完成申请，请您处理`;
          case 'cc':
            return `${applicantName}提交【${businessName}】里程碑完成申请已通过，请您知晓`;
          case 'approved':
            return `您提交【${businessName}】里程碑完成申请已通过`;
          case 'rejected':
            return `您提交【${businessName}】里程碑完成申请已拒绝`;
          default:
            return '审批通知';
        }

      case 'project_create':
        switch (type) {
          case 'pending':
            return `${applicantName}申请创建【${businessName}】，请您处理`;
          case 'cc':
            return `${applicantName}申请创建【${businessName}】已通过，请您知晓`;
          case 'approved':
            return `您申请创建【${businessName}】已通过`;
          case 'rejected':
            return `您申请创建【${businessName}】已拒绝`;
          default:
            return '审批通知';
        }

      case 'contribution_create':
        switch (type) {
          case 'pending':
            return `${applicantName}申请立项【${businessName}】，请您处理`;
          case 'cc':
            return `${applicantName}申请立项【${businessName}】已通过，请您知晓`;
          case 'approved':
            return `您申请立项【${businessName}】已通过`;
          case 'rejected':
            return `您申请立项【${businessName}】已拒绝`;
          default:
            return '审批通知';
        }

      case 'performance_acceptance':
        switch (type) {
          case 'pending':
            return `${applicantName}申请【${businessName}】业绩验收，请您处理`;
          case 'cc':
            return `${applicantName}申请【${businessName}】业绩验收已通过，请您知晓`;
          case 'approved':
            return `您申请【${businessName}】业绩验收已通过`;
          case 'rejected':
            return `您申请【${businessName}】业绩验收已拒绝`;
          default:
            return '审批通知';
        }

      default:
        // 其他审批类型使用通用格式
        switch (type) {
          case 'pending':
            return `${applicantName}申请【${businessName}】，请您处理`;
          case 'cc':
            return `${applicantName}申请【${businessName}】已通过，请您知晓`;
          case 'approved':
            return `您申请【${businessName}】已通过`;
          case 'rejected':
            return `您申请【${businessName}】已拒绝`;
          default:
            return '审批通知';
        }
    }
  }

  function getNotificationTypeText(type: string): string {
    const texts = {
      'pending': '待处理',
      'approved': '已通过',
      'rejected': '已拒绝',
      'cc': '抄送',
    };
    return texts[type] || type;
  }

  function getNotificationTypeColor(type: string): string {
    const colors = {
      'pending': 'orange',
      'approved': 'green',
      'rejected': 'red',
      'cc': 'blue',
    };
    return colors[type] || 'gray';
  }

  function formatTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return '刚刚';
    if (minutes < 60) return `${minutes}分钟前`;
    if (hours < 24) return `${hours}小时前`;
    if (days < 7) return `${days}天前`;
    return date.toLocaleDateString();
  }

  async function handleItemClick(item: MessageItemData) {
    const notificationType = item.notificationType;

    // 不同类型的通知，点击处理方式不同
    if (notificationType === 'pending') {
      // pending 通知：跳转详情页，不标记已读（保留通知）
      history.push(`/approval-center/${item.approvalId}`);
    } else {
      // approved/rejected/cc 通知：跳转详情页 + 自动标记已读
      await markNotificationAsRead(item.id);
      history.push(`/approval-center/${item.approvalId}`);
      // 刷新通知列表
      fetchNotifications(false);
    }
  }

  async function handleMarkAsRead(
    item: MessageItemData,
    event: React.MouseEvent<HTMLElement>
  ) {
    // 阻止事件冒泡，避免触发 itemClick
    event.stopPropagation();

    // 标记已读
    await markNotificationAsRead(item.id);

    // 刷新通知列表
    fetchNotifications(false);
  }

  function handleSeeMore() {
    history.push('/approval-center');
  }

  function clearReconnectTimer() {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }

  function removeSubscriptionChannel(targetChannel?: RealtimeChannel) {
    const channel = targetChannel || channelRef.current;
    if (!channel) return;
    if (closingChannelsRef.current.has(channel)) return;

    closingChannelsRef.current.add(channel);

    // 先断开本地引用，避免 removeChannel 触发 CLOSED 回调时重入清理
    if (channelRef.current === channel) {
      channelRef.current = null;
    }

    Promise.resolve(supabase.removeChannel(channel))
      .catch((error) => {
        listLogger.warn('[Realtime List] 移除频道失败', error);
      })
      .finally(() => {
        closingChannelsRef.current.delete(channel);
      });
  }

  function scheduleReconnect(delay = 3000) {
    if (isUnmountedRef.current || reconnectTimerRef.current !== null) {
      return;
    }

    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = null;
      setupRealtimeSubscription(true);
    }, delay);
  }

  // 设置 Realtime 订阅
  async function setupRealtimeSubscription(isRetry = false) {
    try {
      if (isUnmountedRef.current) {
        return;
      }

      clearReconnectTimer();
      removeSubscriptionChannel();

      // 获取当前用户 ID 和 session
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        listLogger.warn('[Realtime] 用户未登录，无法订阅通知');
        return;
      }

      const userId = session.user.id;
      listLogger.info('[Realtime] 开始设置通知订阅', { userId, isRetry });
      listLogger.debug('[Realtime] 当前用户 JWT token', session.access_token ? 'exists' : 'missing');

      // 创建 Realtime channel（添加更多配置以增强稳定性）
      const channel = supabase
        .channel(`user-notifications-list-${userId}-${Date.now()}`, {
          config: {
            presence: { key: userId },
            broadcast: { self: true },
          },
        })
        .on(
          'postgres_changes',
          {
            event: '*', // 监听所有事件（INSERT, UPDATE, DELETE）
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userId}`,
          },
          (payload) => {
            listLogger.info('[Realtime List] 📨 收到通知变更事件', {
              eventType: payload.eventType,
              table: payload.table,
              schema: payload.schema,
              old: payload.old,
              new: payload.new,
              timestamp: new Date().toISOString(),
            });

            // 根据事件类型处理
            if (payload.eventType === 'INSERT') {
              // 新通知：刷新列表
              listLogger.info('[Realtime List] ✨ INSERT 事件，刷新通知列表');
              fetchNotifications(false);
            } else if (payload.eventType === 'DELETE') {
              // 通知删除：刷新列表（pending 通知被删除）
              listLogger.info('[Realtime List] 🗑️ DELETE 事件，刷新通知列表');
              fetchNotifications(false);
            } else if (payload.eventType === 'UPDATE') {
              // 通知更新（如标记已读）：刷新列表
              listLogger.info('[Realtime List] 🔄 UPDATE 事件，刷新通知列表');
              fetchNotifications(false);
            }
          }
        )
        .subscribe((status, err) => {
          listLogger.info('[Realtime List] 订阅状态变更', { status, error: err });
          const isActiveChannel = channelRef.current === channel;
          const isChannelClosing = closingChannelsRef.current.has(channel);

          if (status === 'SUBSCRIBED') {
            listLogger.info('[Realtime List] ✅ 通知订阅成功', { userId });
            // 订阅成功后，主动获取一次通知列表（防止错过订阅前创建的通知）
            listLogger.info('[Realtime List] 订阅成功，主动获取通知列表');
            fetchNotifications(false);
          } else if (status === 'CLOSED') {
            if (isChannelClosing) {
              listLogger.debug('[Realtime List] 频道在主动清理中，忽略 CLOSED 事件');
              return;
            }
            if (!isActiveChannel) {
              listLogger.debug('[Realtime List] 忽略非活跃频道的 CLOSED 事件');
              return;
            }
            listLogger.warn('[Realtime List] ⚠️ 通知订阅已关闭，尝试重新订阅');
            removeSubscriptionChannel(channel);
            scheduleReconnect(1000);
          } else if (status === 'CHANNEL_ERROR') {
            if (!isActiveChannel) {
              listLogger.debug('[Realtime List] 忽略非活跃频道的 CHANNEL_ERROR 事件');
              return;
            }
            listLogger.error('[Realtime List] ❌ 订阅错误', err);
            removeSubscriptionChannel(channel);
            scheduleReconnect(5000);
          } else if (status === 'TIMED_OUT') {
            if (!isActiveChannel) {
              listLogger.debug('[Realtime List] 忽略非活跃频道的 TIMED_OUT 事件');
              return;
            }
            listLogger.warn('[Realtime List] ⏰ 订阅超时，尝试重新订阅');
            removeSubscriptionChannel(channel);
            scheduleReconnect(3000);
          }
        });

      channelRef.current = channel;
    } catch (error) {
      listLogger.error('[Realtime] 设置订阅失败', error);
      scheduleReconnect(5000);
    }
  }

  useEffect(() => {
    isUnmountedRef.current = false;
    // 初始加载通知
    fetchNotifications();

    // 设置 Realtime 订阅
    setupRealtimeSubscription();

    // 监听审批操作删除通知的事件
    const handleNotificationsDeleted = (event: CustomEvent) => {
      const { deletedIds } = event.detail;

      // 从本地状态中移除被删除的通知
      setNotifications(prevNotifications =>
        prevNotifications.filter(notification => !deletedIds.includes(notification.id))
      );
    };

    // 添加事件监听器
    window.addEventListener('notifications-deleted', handleNotificationsDeleted as EventListener);

    // 清理：组件卸载时取消订阅和事件监听
    return () => {
      isUnmountedRef.current = true;
      clearReconnectTimer();
      removeSubscriptionChannel();
      listLogger.info('[Realtime] 通知订阅已取消');
      window.removeEventListener('notifications-deleted', handleNotificationsDeleted as EventListener);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles['message-box']}>
      <Spin loading={loading} style={{ display: 'block' }}>
        <MessageList
          data={notifications}
          onItemClick={handleItemClick}
          onMarkAsRead={handleMarkAsRead}
          onSeeMoreClick={handleSeeMore}
        />
      </Spin>
    </div>
  );
}

interface MessageBoxProps {
  children: React.ReactNode;
}

function MessageBox({ children }: MessageBoxProps) {
  const [unreadCount, setUnreadCount] = useState(0);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const closingChannelsRef = useRef(new Set<RealtimeChannel>());
  const reconnectTimerRef = useRef<number | null>(null);
  const fallbackPollTimerRef = useRef<number | null>(null);
  const isUnmountedRef = useRef(false);

  async function fetchUnreadCount() {
    badgeLogger.info('[MessageBox Badge] 🔄 开始获取未读数量');
    try {
      const count = await getUnreadNotificationCount();
      badgeLogger.info('[MessageBox Badge] ✅ 获取到的未读数量', count);
      setUnreadCount(count);
      return count;
    } catch (error) {
      badgeLogger.error('[MessageBox Badge] ❌ 获取未读通知数量失败', error);
      throw error; // 重新抛出错误以便上层捕获
    }
  }

  function clearReconnectTimer() {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }

  function clearFallbackPolling() {
    if (fallbackPollTimerRef.current !== null) {
      window.clearInterval(fallbackPollTimerRef.current);
      fallbackPollTimerRef.current = null;
      badgeLogger.info('[Realtime Badge] 已退出失败兜底轮询模式');
    }
  }

  function startFallbackPolling() {
    if (isUnmountedRef.current || fallbackPollTimerRef.current !== null) {
      return;
    }

    badgeLogger.warn('[Realtime Badge] 进入失败兜底轮询模式（30秒）');
    fallbackPollTimerRef.current = window.setInterval(() => {
      if (isUnmountedRef.current) return;
      fetchUnreadCount().catch((error) => {
        badgeLogger.warn('[Realtime Badge] 兜底轮询刷新未读数量失败', error);
      });
    }, 30000);
  }

  function removeBadgeChannel(targetChannel?: RealtimeChannel) {
    const channel = targetChannel || channelRef.current;
    if (!channel) return;
    if (closingChannelsRef.current.has(channel)) return;

    closingChannelsRef.current.add(channel);

    // 先断开本地引用，避免 removeChannel 触发 CLOSED 回调时重入清理
    if (channelRef.current === channel) {
      channelRef.current = null;
    }

    Promise.resolve(supabase.removeChannel(channel))
      .catch((error) => {
        badgeLogger.warn('[Realtime Badge] 移除频道失败', error);
      })
      .finally(() => {
        closingChannelsRef.current.delete(channel);
      });
  }

  function scheduleReconnect(delay = 3000) {
    if (isUnmountedRef.current || reconnectTimerRef.current !== null) {
      return;
    }

    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = null;
      setupBadgeRealtimeSubscription(true);
    }, delay);
  }

  // 设置徽标数 Realtime 订阅
  async function setupBadgeRealtimeSubscription(isRetry = false) {
    try {
      if (isUnmountedRef.current) {
        return;
      }

      clearReconnectTimer();
      removeBadgeChannel();

      // 获取当前用户 ID 和 session
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        badgeLogger.warn('[Realtime Badge] 用户未登录，无法订阅徽标更新');
        return;
      }

      const userId = session.user.id;
      badgeLogger.info('[Realtime Badge] 开始设置徽标订阅', { userId, isRetry });
      badgeLogger.debug('[Realtime Badge] 当前用户 JWT token', session.access_token ? 'exists' : 'missing');

      // 创建 Realtime channel（添加更多配置以增强稳定性）
      const channel = supabase
        .channel(`user-notifications-badge-${userId}-${Date.now()}`, {
          config: {
            presence: { key: userId },
            broadcast: { self: true },
          },
        })
        .on(
          'postgres_changes',
          {
            event: '*', // 监听所有事件（INSERT, UPDATE, DELETE）
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userId}`,
          },
          (payload) => {
            const eventData = {
              eventType: payload.eventType,
              old: payload.old,
              new: payload.new,
              timestamp: new Date().toISOString(),
            };

            badgeLogger.info('[Realtime Badge] 📨 收到通知变更事件', eventData);
            badgeLogger.debug('[Realtime Badge] 新通知详情', payload.new);

            // 根据事件类型处理徽标数
            if (payload.eventType === 'INSERT') {
              // 新通知：刷新徽标数
              badgeLogger.info('[Realtime Badge] ✨ INSERT 事件，开始刷新徽标数');
              fetchUnreadCount().then(() => {
                badgeLogger.info('[Realtime Badge] ✅ 徽标数刷新完成');
              }).catch((err) => {
                badgeLogger.error('[Realtime Badge] ❌ 徽标数刷新失败', err);
              });
            } else if (payload.eventType === 'DELETE') {
              // 通知删除：刷新徽标数（pending 通知被删除）
              badgeLogger.info('[Realtime Badge] 🗑️ DELETE 事件，开始刷新徽标数');
              fetchUnreadCount();
            } else if (payload.eventType === 'UPDATE') {
              // 通知更新（如标记已读）：刷新徽标数
              badgeLogger.info('[Realtime Badge] 🔄 UPDATE 事件，开始刷新徽标数');
              fetchUnreadCount();
            }
          }
        )
        .subscribe((status, err) => {
          badgeLogger.info('[Realtime Badge] 订阅状态变更', { status, error: err });
          const isActiveChannel = channelRef.current === channel;
          const isChannelClosing = closingChannelsRef.current.has(channel);

          if (status === 'SUBSCRIBED') {
            badgeLogger.info('[Realtime Badge] ✅ 徽标订阅成功', { userId });
            clearFallbackPolling();
            // 订阅成功后，主动获取一次未读数量（防止错过订阅前创建的通知）
            badgeLogger.info('[Realtime Badge] 订阅成功，主动获取未读数量');
            fetchUnreadCount();
          } else if (status === 'CLOSED') {
            if (isChannelClosing) {
              badgeLogger.debug('[Realtime Badge] 频道在主动清理中，忽略 CLOSED 事件');
              return;
            }
            if (!isActiveChannel) {
              badgeLogger.debug('[Realtime Badge] 忽略非活跃频道的 CLOSED 事件');
              return;
            }
            badgeLogger.warn('[Realtime Badge] ⚠️ 徽标订阅已关闭，尝试重新订阅');
            startFallbackPolling();
            fetchUnreadCount().catch((error) => {
              badgeLogger.warn('[Realtime Badge] CLOSED 后兜底刷新失败', error);
            });
            removeBadgeChannel(channel);
            scheduleReconnect(1000);
          } else if (status === 'CHANNEL_ERROR') {
            if (!isActiveChannel) {
              badgeLogger.debug('[Realtime Badge] 忽略非活跃频道的 CHANNEL_ERROR 事件');
              return;
            }
            badgeLogger.error('[Realtime Badge] ❌ 订阅错误', err);
            startFallbackPolling();
            fetchUnreadCount().catch((error) => {
              badgeLogger.warn('[Realtime Badge] CHANNEL_ERROR 后兜底刷新失败', error);
            });
            removeBadgeChannel(channel);
            scheduleReconnect(5000);
          } else if (status === 'TIMED_OUT') {
            if (!isActiveChannel) {
              badgeLogger.debug('[Realtime Badge] 忽略非活跃频道的 TIMED_OUT 事件');
              return;
            }
            badgeLogger.warn('[Realtime Badge] ⏰ 订阅超时，尝试重新订阅');
            startFallbackPolling();
            fetchUnreadCount().catch((error) => {
              badgeLogger.warn('[Realtime Badge] TIMED_OUT 后兜底刷新失败', error);
            });
            removeBadgeChannel(channel);
            scheduleReconnect(3000);
          }
        });

      channelRef.current = channel;
    } catch (error) {
      badgeLogger.error('[Realtime Badge] 设置订阅失败', error);
      scheduleReconnect(5000);
    }
  }

  useEffect(() => {
    isUnmountedRef.current = false;
    fetchUnreadCount().catch((error) => {
      badgeLogger.warn('[Realtime Badge] 初始化未读数量失败', error);
    });
    // 设置 Realtime 订阅（订阅成功后会自动获取未读数量）
    setupBadgeRealtimeSubscription();

    // 监听审批操作删除通知的事件
    const handleNotificationsDeleted = (event: CustomEvent) => {
      const { deletedIds } = event.detail;
      badgeLogger.info('[Realtime Badge] 📨 收到 notifications-deleted 自定义事件', deletedIds);

      // 减少徽标数（被删除的通知都是 pending 通知，每个计数1）
      setUnreadCount(prevCount => {
        const newCount = Math.max(0, prevCount - deletedIds.length);
        badgeLogger.info('[Realtime Badge] 徽标数更新', { prevCount, newCount });
        return newCount;
      });
    };

    // 监听审批处理完成事件
    const handleApprovalProcessed = () => {
      badgeLogger.info('[Realtime Badge] 📨 收到 approval-processed 事件，刷新徽标数');
      // 延迟一下再刷新，确保后端通知已删除
      window.setTimeout(() => {
        fetchUnreadCount();
      }, 500);
    };

    // 添加事件监听器
    window.addEventListener('notifications-deleted', handleNotificationsDeleted as EventListener);
    window.addEventListener('approval-processed', handleApprovalProcessed);
    badgeLogger.info('[Realtime Badge] ✅ 已注册 notifications-deleted 和 approval-processed 事件监听器');

    // 清理：组件卸载时取消订阅和事件监听
    return () => {
      isUnmountedRef.current = true;
      clearReconnectTimer();
      clearFallbackPolling();
      removeBadgeChannel();
      badgeLogger.info('[Realtime Badge] 🔌 徽标订阅已取消');
      window.removeEventListener('notifications-deleted', handleNotificationsDeleted as EventListener);
      window.removeEventListener('approval-processed', handleApprovalProcessed);
      badgeLogger.info('[Realtime Badge] 🔌 已移除事件监听器');
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 监控 unreadCount 变化（用于调试）
  useEffect(() => {
    badgeLogger.debug('[MessageBox Badge] unreadCount 更新为', unreadCount);
  }, [unreadCount]);

  return (
    <Trigger
      trigger="hover"
      popup={() => <DropContent />}
      position="br"
      unmountOnExit={false}
      popupAlign={{ bottom: 4 }}
    >
      <Badge count={unreadCount} dot={false} maxCount={99}>
        {children}
      </Badge>
    </Trigger>
  );
}

export default MessageBox;
